import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { FileImage, LockKeyhole, Upload } from 'lucide-react';

interface DropzoneProps {
  onFile: (file: File) => void;
  compact?: boolean;
  disabled?: boolean;
}

export function Dropzone({ onFile, compact = false, disabled = false }: DropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptFiles = (files: FileList | null): void => {
    const file = files?.item(0);
    if (file) onFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) acceptFiles(event.dataTransfer.files);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>): void => acceptFiles(event.currentTarget.files);

  return (
    <label
      className={`dropzone${dragging ? ' dropzone--dragging' : ''}${compact ? ' dropzone--compact' : ''}`}
      onDragEnter={() => setDragging(true)}
      onDragLeave={() => setDragging(false)}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
        onChange={onChange}
        disabled={disabled}
        aria-describedby="file-constraints privacy-note"
      />
      <span className="dropzone__icon" aria-hidden="true">
        {compact ? <FileImage size={24} /> : <Upload size={28} strokeWidth={1.7} />}
      </span>
      <span className="dropzone__copy">
        <strong>{compact ? 'Choose another image' : 'Drop an image to trace its evidence'}</strong>
        <span>{compact ? 'JPEG or PNG' : 'or choose a file from your device'}</span>
      </span>
      {!compact && <span className="dropzone__button">Choose image</span>}
      {!compact && (
        <span className="dropzone__privacy" id="privacy-note">
          <LockKeyhole size={14} aria-hidden="true" />
          Never stored. Verification stays in-browser unless you enable optional measurement.
        </span>
      )}
      <span className="sr-only" id="file-constraints">JPEG or PNG, up to 25 megabytes and 40 megapixels.</span>
    </label>
  );
}
